import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { toBackendErrorReason, toBackendFailureReason } from '@shared/utils';
import { ReportDownloadError } from '@features/users/util-reports';

/**
 * A download request asks for a blob, so a rejection (409, 410, ...) arrives
 * as a Blob holding the backend's JSON `ResponseArgs`; it is parsed to get
 * the reason (#5248).
 */
export async function toReportDownloadError(error: unknown): Promise<ReportDownloadError> {
  if (!(error instanceof HttpErrorResponse)) return { status: 0, reason: undefined };
  const reason = (await readBlobReason(error)) ?? toBackendErrorReason(error);
  return { status: error.status, reason };
}

async function readBlobReason(error: HttpErrorResponse): Promise<string | undefined> {
  if (!(error.error instanceof Blob)) return undefined;
  try {
    return toBackendFailureReason(JSON.parse(await error.error.text()));
  } catch {
    return undefined;
  }
}

/**
 * The file name of a `Content-Disposition` header, or the fallback. The
 * encoded `filename*=` wins over the plain `filename=` (ASP.NET sends both);
 * the plain one is taken as it is, so a `%` in it cannot fail the download (#5248).
 */
export function fileNameFromDisposition(headers: HttpHeaders, fallback: string): string {
  const disposition = headers.get('Content-Disposition') ?? '';
  const encoded = /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1];
  const plain = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  return (encoded && decodeFileName(encoded)) || plain || fallback;
}

function decodeFileName(encoded: string): string | undefined {
  try {
    return decodeURIComponent(encoded);
  } catch {
    return undefined;
  }
}
